import { requestGetCustomerByIdRequest } from '../api/customer/get';
import { createRequestUpdateCustomer } from '../api/customer/update';
import translateErrors from '../api/customer/translate-errors';
import { getCookie } from '../commons/cookie';
import { path, pathOr, contains, omit } from 'ramda';
import axios from 'axios';
import onlyNumbers from '../commons/utils/only-numbers';

// TODO: Remover depois que a API de Customer remover estas constraints
// Falar com Goni
const OLD_CONSTRAINTS_MAP = {
  WITHOUT_MAIN_PHONE: 'phone',
  INVALID_DOCUMENT_NUMBER: 'cpf',
  DOCUMENT_NUMER_REQUIRED: 'cpf',
  BIRTHDAY_REQUIRED: 'birthday'
};
const NEW_CONSTRAINTS_MAP = {
  INVALID_NAME_LENGTH: 'name',
  REQUIRED_MAIN_PHONE: 'phone',
  REQUIRED_DOCUMENT_NUMBER: 'cpf',
  REQUIRED_BIRTHDAY: 'birthday'
};
const CONSTRAINTS_MAP = { ...OLD_CONSTRAINTS_MAP, ...NEW_CONSTRAINTS_MAP };

function isAcceptedConstraint(constraint) {
  // TODO: Remover depois que a API de Customer remover estas constraints
  // Falar com Goni
  const oldConstraints = [
    'STATEINSCRIPTION_REQUIRED',
    'INDICATORIERECIPIENT_REQUIRED',
    'MAIN_ADDRESS_REQUIRED',
    'INVALID_RESPONSIBLE_NAME_LENGTH'
  ];
  const newContraints = ['REQUIRED_STATEINSCRIPTION', 'REQUIRED_INDICATORIERECIPIENT'];

  return !contains(constraint.code, [...oldConstraints, ...newContraints]);
}

function getValidConstraints(apiCustomer) {
  if (!path(['data', '_constraints'], apiCustomer)) {
    return [];
  }
  return apiCustomer.data._constraints.filter(isAcceptedConstraint);
}

function hasValidConstraints(apiCustomer) {
  return getValidConstraints(apiCustomer).length > 0;
}

function parseConstraints(apiCustomer) {
  if (!hasValidConstraints(apiCustomer)) {
    return {};
  }
  return getValidConstraints(apiCustomer).reduce(function (errors, constraint) {
    return {
      ...errors,
      [CONSTRAINTS_MAP[constraint.code]]: constraint.message
    };
  }, {});
}

export function parsePhone(phone) {
  if (!phone) {
    return undefined;
  }
  const digits = phone.replace(/\D/g, '');
  return {
    ddd: digits.substr(0, 2),
    number: digits.substr(2)
  };
}

const getPhones = (mainPhone, secondaryPhone, receiveWhatsApp) => {
  const main = parsePhone(mainPhone);

  if (secondaryPhone && receiveWhatsApp === 'true') {
    const cellphone = parsePhone(secondaryPhone);
    return { main, cellphone };
  }

  return { main };
};

function getUpdateCustomerBody(customer) {
  const { offersEmail, receiveWhatsApp, name, gender, birthday, cpf, phone, cellphone } = customer;

  const customerBody = {
    optIn: {
      mailing: offersEmail,
      receiveWhatsApp: receiveWhatsApp
    },
    telephones: getPhones(phone, cellphone, receiveWhatsApp),
    type: {
      pf: {
        fullName: name,
        gender: gender,
        birthday: birthday
      }
    }
  };

  if (cpf) {
    return {
      ...customerBody,
      type: {
        ...customerBody.type,
        pf: {
          cpf: onlyNumbers(cpf),
          ...customerBody.type.pf
        }
      }
    };
  } else {
    return customerBody;
  }
}

async function doUpdateCustomer(customer) {
  const customerToken = getCookie('customerToken');
  try {
    await axios(
      createRequestUpdateCustomer(customer.id, customerToken, getUpdateCustomerBody(customer))
    );
    return {
      success: true,
      errors: {}
    };
  } catch (err) {
    const { validationErrors } = pathOr({}, ['response', 'data'], err);
    const errors = translateErrors(validationErrors);
    return {
      success: false,
      errors: errors
    };
  }
}

export function normalizeCustomer(customer) {
  return {
    id: path(['data', 'id'], customer),
    email: path(['data', 'email'], customer),
    cpf: path(['data', 'type', 'pf', 'cpf'], customer),
    name: path(['data', 'type', 'pf', 'fullName'], customer),
    gender: path(['data', 'type', 'pf', 'gender'], customer),
    birthday: path(['data', 'type', 'pf', 'birthday'], customer),
    phone:
      path(['data', 'telephones', 'main', 'ddd'], customer) +
      path(['data', 'telephones', 'main', 'number'], customer),
    cellphone:
      path(['data', 'telephones', 'cellphone', 'ddd'], customer) +
      path(['data', 'telephones', 'cellphone', 'number'], customer),
    receiveWhatsApp: path(['data', 'optIn', 'receiveWhatsApp'], customer) ? 'true' : false
  };
}

export async function getCustomerFromApi() {
  const customerId = getCookie('customerId');
  const customerToken = getCookie('customerToken');
  try {
    return await axios(requestGetCustomerByIdRequest({ customerId, customerToken }));
  } catch (err) {
    console.error('error accessing customer api', err);
    return Promise.reject(err);
  }
}

function removeCpfFromCustomerIfNeeded(customer, customerAlreadyHasCpf) {
  if (customerAlreadyHasCpf) {
    return omit(['cpf'], customer);
  }
  return customer;
}

export async function updateCustomer(customer, customerAlreadyHasCpf = false) {
  const customerToBeUpdated = removeCpfFromCustomerIfNeeded(customer, customerAlreadyHasCpf);
  const updateResponse = await doUpdateCustomer(customerToBeUpdated);
  if (updateResponse.success) {
    const apiCustomer = await axios(
      requestGetCustomerByIdRequest({
        customerId: customer.id,
        customerToken: getCookie('customerToken')
      })
    );
    return {
      success: !hasValidConstraints(apiCustomer),
      errors: parseConstraints(apiCustomer),
      customer: apiCustomer
    };
  } else {
    return updateResponse;
  }
}
